import React, { useState, useEffect, useRef } from 'react';
import { FaVolumeMute, FaVolumeUp, FaInstagram, FaTwitter, FaPlay, FaPause, FaForward, FaBackward, FaMusic, FaTelegram } from 'react-icons/fa';
import './MainContent.css';

const NavBar = ({ 
  audioRef, 
  isPlaying, 
  togglePlayPause, 
  nextTrack, 
  prevTrack, 
  currentTrack,
  musicTracks,
  onTrackSelect,
  setIsPlaying
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMusicList, setShowMusicList] = useState(false);
  const musicListRef = useRef(null);
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Close music list when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (musicListRef.current && !musicListRef.current.contains(event.target)) {
        setShowMusicList(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Mobile menu toggle
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  // Toggle music list
  const toggleMusicList = () => {
    setShowMusicList(!showMusicList);
  };
  
  // Select track and close list
  const selectTrack = (index) => {
    if (typeof onTrackSelect === 'function') {
      onTrackSelect(index);
      // Auto-play the newly selected track
      if (setIsPlaying) {
        setIsPlaying(true);
      } else if (audioRef && audioRef.current) {
        audioRef.current.play().catch(e => console.error("Error playing audio:", e));
      }
    }
    setShowMusicList(false);
  };
  
  // Handle play/pause safely
  const handlePlayPause = () => {
    if (typeof togglePlayPause === 'function') {
      togglePlayPause();
    }
  };
  
  // Handle previous track safely with auto-play
  const handlePrevTrack = () => {
    if (typeof prevTrack === 'function') {
      prevTrack();
      // Auto-play after changing track
      if (setIsPlaying) {
        setIsPlaying(true);
      } else if (audioRef && audioRef.current) {
        audioRef.current.play().catch(e => console.error("Error playing audio:", e));
      }
    }
  };
  
  // Handle next track safely with auto-play
  const handleNextTrack = () => {
    if (typeof nextTrack === 'function') {
      nextTrack();
      // Auto-play after changing track
      if (setIsPlaying) {
        setIsPlaying(true);
      } else if (audioRef && audioRef.current) {
        audioRef.current.play().catch(e => console.error("Error playing audio:", e));
      }
    }
  };
  
  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-container">
        <div className="navbar-logo">
          {/* PNG Logo */}
          <img 
            src="/Untitled-3.png" 
            alt="WiggaChain Logo" 
            className="logo-image" 
          />
          <span className="logo-text">WiggaChain</span>
        </div>
        
        <div className={`navbar-links ${menuOpen ? 'active' : ''}`}>
          <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
          <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
          <a href="#how-to-buy" onClick={() => setMenuOpen(false)}>How to Buy</a>
          <a href="#roadmap" onClick={() => setMenuOpen(false)}>Roadmap</a>
          <a href="#team" onClick={() => setMenuOpen(false)}>Team</a>
          
          <div className="nav-buttons">
            <button className="connect-button">
              <span>Connect Wallet</span>
              <span className="button-glow"></span>
            </button>
            
            <div className="music-controls">
              <button 
                className="music-control-button" 
                onClick={handlePrevTrack} 
                title="Previous Track"
              >
                <FaBackward />
              </button>
              
              <button 
                className="music-control-button" 
                onClick={handlePlayPause} 
                title={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>
              
              <button 
                className="music-control-button" 
                onClick={handleNextTrack} 
                title="Next Track"
              >
                <FaForward />
              </button>
              
              <div className="music-track-selector" ref={musicListRef}>
                <button className="music-list-button" onClick={toggleMusicList} title="Select Track">
                  <FaMusic />
                  <span className="current-track-name">
                    {currentTrack?.title || "WiggaChain Theme"}
                  </span>
                </button>
                
                {showMusicList && (
                  <div className="music-tracks-dropdown">
                    {(musicTracks || []).map((track, index) => (
                      <div 
                        key={index} 
                        className={`track-item ${
                          currentTrack?.title === track.title ? 'active' : ''
                        }`}
                        onClick={() => selectTrack(index)}
                      >
                        {track.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="social-links">
              <a href="https://www.instagram.com/wiggachainonsol/profilecard/?igsh=MWwwMTh3dXc1anltbA==" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <FaInstagram />
              </a>
              <a href="https://twitter.com/wiggachainonSolana" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <FaTwitter />
              </a>
              <a href="https://t.me/wiggachainportal" target="_blank" rel="noopener noreferrer" aria-label="Telegram">
                <FaTelegram />
              </a>
            </div>
          </div>
        </div>
        
        <div className="mobile-menu-toggle" onClick={toggleMenu}>
          <div className={`menu-icon ${menuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;